/**
 * Company: SPHMedia
 * Description: [A customizable carousel component for displaying a list of items]
 */

import cx from "classnames";
import { Children, ReactElement, ReactNode } from "react";

import CustomCarouselButton from "./CustomCarouselButton";
import { useCustomCarousel } from "./hooks/useCustomCarousel";

type CustomCarouselProps = {
  children: ReactNode;
  title: ReactNode;
  slidesPerView?: number;
  gap?: number;
  step?: number;

  showNavigation?: boolean;
  draggable?: boolean;

  className?: string;
  headerClassName?: string;
  viewportClassName?: string;
  trackClassName?: string;
};

/**
 * CustomCarousel
 * Description: [A customizable carousel component for displaying a list of items]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function CustomCarousel({
  children,
  title,
  slidesPerView = 3.2,
  gap = 12,
  step = 1,
  className,
  headerClassName,
  viewportClassName,
  trackClassName,
  showNavigation = true,
  draggable = false,
}: CustomCarouselProps): ReactElement {
  const slides = Children.toArray(children);

  const {
    viewportRef,
    slideWidth,
    translateX,
    next,
    prev,
    canGoNext,
    canGoPrev,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
  } = useCustomCarousel({
    totalSlides: slides.length,
    slidesPerView,
    gap,
    step,
    draggable,
  });

  return (
    <div data-testid="carousel-wrapper" className={cx("w-full", className)}>
      <div
        className={cx(
          "mb-2.5 flex items-center justify-between",
          headerClassName,
        )}
      >
        <div>{title}</div>

        {showNavigation && (
          <div className="flex gap-3">
            <CustomCarouselButton
              direction="prev"
              variant={canGoPrev ? "enabled" : "disabled"}
              onClick={prev}
            />

            <CustomCarouselButton
              direction="next"
              variant={canGoNext ? "enabled" : "disabled"}
              onClick={next}
            />
          </div>
        )}
      </div>

      <div
        data-testid="carousel-viewport"
        ref={viewportRef}
        className={cx("overflow-hidden p-5 bg-gray-500", viewportClassName)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        style={{
          touchAction: draggable ? "pan-y" : undefined,
        }}
      >
        <div
          data-testid="carousel-track"
          className={cx(
            "flex transition-transform duration-300 ease-out",
            trackClassName,
          )}
          style={{
            gap: `${gap}px`,
            transform: `translateX(-${translateX}px)`,
          }}
        >
          {slides.map((child, index) => (
            <div
              data-testid="carousel-slide"
              key={index}
              className="shrink-0"
              style={{
                width: slideWidth,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
